import React from 'react';
import { Circle } from 'react-konva';
import { denormalizePoints } from '../../../hooks/useCoordinates';

export type EditHandlesProps = {
  editingPoints: [number, number][]; // normalized [0..1]
  imageSize: { width: number; height: number };
  selectedPointIndices: number[];
  scale?: number;
  handlePxRadius?: number;
  accentColor?: string;
  /** Mark vertex 0 distinctly (a black inner ring) — used for four-point
   *  types where the clockwise drawing start point matters. */
  markStart?: boolean;
  onPointSelect: (idx: number, extend: boolean) => void;
  onPointDragStart: (idx: number, pos: { x: number; y: number }) => void;
  onPointDrag: (idx: number, pos: { x: number; y: number }) => void;
  onPointDragEnd: () => void;
};

const EditHandles: React.FC<EditHandlesProps> = ({
  editingPoints,
  imageSize,
  selectedPointIndices,
  scale = 1,
  handlePxRadius = 5,
  accentColor = '#1e88e5',
  markStart = false,
  onPointSelect,
  onPointDragStart,
  onPointDrag,
  onPointDragEnd,
}) => {
  const radius = handlePxRadius / scale;
  const stroke = 1.5 / scale;
  const hitRadius = (handlePxRadius * 2) / scale;

  return (
    <>
      {editingPoints.map(([x, y], idx) => {
        const [dx, dy] = denormalizePoints([[x, y]], imageSize)[0];
        const isSelected = selectedPointIndices.includes(idx);
        // A point outside the frame falls outside the Stage's own canvas
        // buffer (width={image.width*scale}) and is never rasterized — Konva
        // has nowhere to draw it, this isn't a CSS clip. Rather than resize
        // the Stage (a coordinate-system change touching every click/drag/
        // marquee handler that divides raw pointer px by imageSize — too
        // risky to ship without being able to visually test the result),
        // clamp a distinct, non-interactive marker to the frame edge so an
        // off-frame point is at least visible and roughly locatable instead
        // of silently vanishing (Rustem, #299). The real handle below stays
        // at its true (off-canvas, so effectively hidden) position — full
        // drag-to-reposition of an off-frame point is a separate, larger
        // follow-up if wanted.
        const isOffFrame = dx < 0 || dx > imageSize.width || dy < 0 || dy > imageSize.height;
        const clampedX = Math.min(Math.max(dx, 0), imageSize.width);
        const clampedY = Math.min(Math.max(dy, 0), imageSize.height);
        return (
          <React.Fragment key={idx}>
            {isOffFrame && (
              <Circle
                x={clampedX}
                y={clampedY}
                radius={radius * 0.8}
                stroke={isSelected ? accentColor : '#ef4444'}
                strokeWidth={stroke}
                dash={[stroke * 2, stroke * 2]}
                listening={false}
              />
            )}
            <Circle
              x={dx}
              y={dy}
              radius={radius}
              hitStrokeWidth={hitRadius}
              // Unselected → white pill with accent ring; selected → filled
              // accent with white ring. No drop shadow — Konva shadowBlur is
              // a per-frame cost we don't want during drag/marquee.
              fill={isSelected ? accentColor : '#ffffff'}
              stroke={isSelected ? '#ffffff' : accentColor}
              strokeWidth={stroke}
              draggable
              onMouseDown={(e) => {
                if (e.evt.shiftKey) {
                  onPointSelect(idx, true);
                } else if (!isSelected) {
                  onPointSelect(idx, false);
                }
              }}
              onClick={(e) => {
                if (e.evt.shiftKey) return;
                onPointSelect(idx, false);
              }}
              onDragStart={(e) => {
                const nx = e.target.x() / imageSize.width;
                const ny = e.target.y() / imageSize.height;
                onPointDragStart(idx, { x: nx, y: ny });
              }}
              onDragMove={(e) => {
                const nx = e.target.x() / imageSize.width;
                const ny = e.target.y() / imageSize.height;
                onPointDrag(idx, { x: nx, y: ny });
              }}
              onDragEnd={onPointDragEnd}
            />
            {/* Start-vertex marker: subtle black inner ring inside the handle,
                so the clockwise drawing start point is distinguishable. Drawn
                non-interactive so it never intercepts drag/select events. */}
            {markStart && idx === 0 && (
              <Circle
                x={dx}
                y={dy}
                radius={radius * 0.45}
                stroke="black"
                strokeWidth={stroke}
                listening={false}
              />
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default EditHandles;
